/* Le fil d'une fiche : ce qu'on y note, ce qu'il reste à faire, ce qui a changé.
 *
 * Le même bloc sur toutes les fiches — client, usine, produit, agent, vente,
 * dossier, réclamation. C'est voulu : on ne veut pas que « suivre l'activité »
 * veuille dire autre chose selon l'écran.
 *
 * Ce qui est à faire passe devant, parce que c'est la seule partie du fil qui
 * attend quelque chose de quelqu'un. */

import { useMemo, useState } from "react";
import { useAgents, useJournal } from "@/lib/donnees";
import { useSession } from "@/lib/session";
import { Empty, Section, Tag } from "@/ui/primitives";
import { Icon } from "@/ui/Icon";
import { aujourdhui, dateFr, echeanceFr, momentFr } from "@/lib/format";
import { ETATS, aFaire, enRetard, fil, phrase } from "@/domain/journal";
import type { EntreeJournal, NatureJournal, SujetJournal } from "@/types/metier";
import {
  ajouterNote, cloreActivite, corrigerNote, planifierActivite, retirerNote, rouvrirActivite,
} from "./actions";

type Vue = "tout" | NatureJournal;

const VUES: { cle: Vue; nom: string }[] = [
  { cle: "tout", nom: "Tout" },
  { cle: "note", nom: "Notes" },
  { cle: "activite", nom: "À faire" },
  { cle: "changement", nom: "Historique" },
];

export function Journal({
  sujet, sujetId, titre = "Suivi", note,
}: {
  sujet: SujetJournal;
  sujetId: string;
  titre?: string;
  note?: string;
}) {
  const { profil } = useSession();
  const { donnees: entrees, chargement, recharger } = useJournal(sujet, sujetId);
  const { donnees: agents } = useAgents();
  const [vue, setVue] = useState<Vue>("tout");
  const [texte, setTexte] = useState("");
  const [mode, setMode] = useState<"note" | "activite">("note");
  const [echeance, setEcheance] = useState("");
  const [assigne, setAssigne] = useState("");
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const nomAgent = useMemo(
    () => new Map(agents.map((a) => [a.id, a.nom] as const)),
    [agents],
  );

  const ouvertes = useMemo(() => aFaire(entrees), [entrees]);
  const visibles = useMemo(
    () => fil(entrees, vue === "tout" ? null : vue).filter(
      (e) => !(vue === "tout" && e.nature === "activite" && e.etat === "a_faire"),
    ),
    [entrees, vue],
  );

  const compte = (n: NatureJournal) => entrees.filter((e) => e.nature === n).length;

  const porteur = profil ? { sujet, sujetId, auteur: profil.id } : null;

  const agir = async (fn: () => Promise<void>) => {
    setOccupe(true);
    setErreur(null);
    try {
      await fn();
      recharger();
    } catch (e) {
      setErreur(e instanceof Error ? e.message : "Écriture refusée.");
    } finally {
      setOccupe(false);
    }
  };

  const envoyer = () => {
    if (!porteur || !texte.trim()) return;
    void agir(async () => {
      if (mode === "note") {
        await ajouterNote(porteur, texte);
      } else {
        await planifierActivite(porteur, {
          texte,
          echeance: echeance || null,
          assigne_a: assigne || porteur.auteur,
        });
      }
      setTexte("");
      setEcheance("");
      setAssigne("");
    });
  };

  return (
    <Section
      titre={titre}
      note={note ?? (ouvertes.length ? `${ouvertes.length} à faire` : "rien en attente")}
    >
      <div className="card card-pad journal">
        {erreur ? <div className="banner">{erreur}</div> : null}

        {/* ── ce qu'il reste à faire ── */}
        {ouvertes.length ? (
          <ul className="jr-todo">
            {ouvertes.map((e) => (
              <li key={e.id} className={enRetard(e) ? "jr-todo-l retard" : "jr-todo-l"}>
                <button
                  type="button"
                  className="jr-case"
                  title="Marquer comme faite"
                  disabled={occupe || !porteur}
                  onClick={() => void agir(() => cloreActivite(e.id, true))}
                >
                  <Icon n="coche" cls="" />
                </button>
                <div className="jr-todo-c">
                  <div className="jr-txt">{e.texte}</div>
                  <div className="jr-meta">
                    <Icon n="horloge" cls="" />
                    <span className={enRetard(e) ? "retard-t" : ""}>
                      {e.echeance ? `${dateFr(e.echeance)} · ${echeanceFr(e.echeance)}` : "sans échéance"}
                    </span>
                    {e.assigne_a ? <> · {nomAgent.get(e.assigne_a) ?? "—"}</> : null}
                  </div>
                </div>
                <button
                  type="button"
                  className="jr-x"
                  title="Annuler"
                  disabled={occupe || !porteur}
                  onClick={() => void agir(() => cloreActivite(e.id, false))}
                >
                  <Icon n="fermer" cls="" />
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        {/* ── écrire ── */}
        {porteur ? (
          <div className="jr-saisie">
            {/* Deux pictos, sans libellé : le bouton d'envoi dit déjà lequel
                est choisi, et le répéter ici encombrait pour rien. Le nom
                reste au survol et pour les lecteurs d'écran. */}
            <div className="jr-bascule">
              <button
                type="button"
                className="pill pill-ico"
                aria-pressed={mode === "note"}
                aria-label="Noter"
                title="Noter"
                onClick={() => setMode("note")}
              >
                <Icon n="note" cls="" />
              </button>
              <button
                type="button"
                className="pill pill-ico"
                aria-pressed={mode === "activite"}
                aria-label="Planifier une activité"
                title="Planifier une activité"
                onClick={() => setMode("activite")}
              >
                <Icon n="horloge" cls="" />
              </button>
            </div>
            <textarea
              className="jr-champ"
              rows={2}
              placeholder={
                mode === "note"
                  ? "Ce qui s'est dit, ce qu'il faut retenir…"
                  : "Ce qu'il reste à faire…"
              }
              value={texte}
              onChange={(ev) => setTexte(ev.target.value)}
            />
            {mode === "activite" ? (
              <div className="jr-quand">
                <label className="jr-lab">
                  Échéance
                  <input
                    type="date"
                    className="jr-date"
                    min={aujourdhui()}
                    value={echeance}
                    onChange={(ev) => setEcheance(ev.target.value)}
                  />
                </label>
                <label className="jr-lab">
                  Pour
                  <select
                    className="jr-sel"
                    value={assigne}
                    onChange={(ev) => setAssigne(ev.target.value)}
                  >
                    <option value="">moi</option>
                    {agents.filter((a) => a.actif).map((a) => (
                      <option key={a.id} value={a.id}>{a.nom}</option>
                    ))}
                  </select>
                </label>
              </div>
            ) : null}
            <div className="jr-actions">
              <button
                type="button"
                className="btn btn-primary"
                disabled={occupe || !texte.trim()}
                onClick={envoyer}
              >
                <Icon n="envoyer" cls="" />
                {mode === "note" ? "Noter" : "Planifier"}
              </button>
            </div>
          </div>
        ) : null}

        {/* ── le fil ── */}
        <div className="jr-vues">
          {VUES.map((v) => (
            <button
              key={v.cle}
              type="button"
              className="pill"
              aria-pressed={vue === v.cle}
              onClick={() => setVue(v.cle)}
            >
              {v.nom}
              {v.cle !== "tout" ? <span className="n">{compte(v.cle)}</span> : null}
            </button>
          ))}
        </div>

        {chargement ? (
          <div className="chargement">Chargement…</div>
        ) : visibles.length ? (
          <ul className="jr-fil">
            {visibles.map((e) => (
              <Entree
                key={e.id}
                e={e}
                nomAgent={nomAgent}
                moi={profil?.id ?? null}
                occupe={occupe}
                agir={agir}
              />
            ))}
          </ul>
        ) : (
          <Empty
            titre="Rien dans le fil"
            detail={
              vue === "changement"
                ? "Aucune modification enregistrée sur cette fiche."
                : "Notez ce qui s'est passé : tout le monde le verra ici."
            }
          />
        )}
      </div>
    </Section>
  );
}

function Entree({
  e, nomAgent, moi, occupe, agir,
}: {
  e: EntreeJournal;
  nomAgent: ReadonlyMap<string, string>;
  moi: string | null;
  occupe: boolean;
  agir: (fn: () => Promise<void>) => Promise<void>;
}) {
  const [edite, setEdite] = useState<string | null>(null);
  const sien = e.auteur != null && e.auteur === moi;

  return (
    <li className={`jr-e jr-${e.nature}`}>
      <span className="jr-puce">
        <Icon
          n={e.nature === "note" ? "note" : e.nature === "activite" ? "horloge" : "crayon"}
          cls=""
        />
      </span>
      <div className="jr-corps">
        {edite !== null ? (
          <div className="jr-saisie-l">
            <textarea
              className="jr-champ"
              rows={2}
              value={edite}
              onChange={(ev) => setEdite(ev.target.value)}
            />
            <div className="jr-actions">
              <button
                type="button"
                className="btn btn-primary"
                disabled={occupe}
                onClick={() =>
                  void agir(async () => {
                    await corrigerNote(e.id, edite);
                    setEdite(null);
                  })
                }
              >
                Enregistrer
              </button>
              <button type="button" className="btn" onClick={() => setEdite(null)}>
                Annuler
              </button>
            </div>
          </div>
        ) : (
          <div className="jr-txt">{phrase(e)}</div>
        )}
        <div className="jr-meta">
          {e.auteur ? (nomAgent.get(e.auteur) ?? "—") : "l'application"}
          {" · "}
          {momentFr(e.cree_le)}
          {e.nature === "activite" && e.etat && e.etat !== "a_faire" ? (
            <> · <Tag ton={e.etat === "faite" ? "ok" : ""}>{ETATS[e.etat]}</Tag></>
          ) : null}
        </div>
      </div>
      {e.nature === "note" && sien && edite === null ? (
        <div className="jr-outils">
          <button
            type="button"
            className="jr-x"
            title="Corriger"
            onClick={() => setEdite(e.texte ?? "")}
          >
            <Icon n="crayon" cls="" />
          </button>
          <button
            type="button"
            className="jr-x"
            title="Supprimer"
            disabled={occupe}
            onClick={() => void agir(() => retirerNote(e.id))}
          >
            <Icon n="corbeille" cls="" />
          </button>
        </div>
      ) : null}
      {e.nature === "activite" && e.etat === "faite" ? (
        <div className="jr-outils">
          <button
            type="button"
            className="jr-x"
            title="Rouvrir"
            disabled={occupe}
            onClick={() => void agir(() => rouvrirActivite(e.id))}
          >
            <Icon n="horloge" cls="" />
          </button>
        </div>
      ) : null}
    </li>
  );
}
